import { Card, Flex, Tooltip, Typography } from "antd";
import { DollarOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Bar } from "@ant-design/plots";
import type { DashboardPeriod } from "../../utils";
import { estadoDeWidget, WidgetPlaceholder } from "../widget-state";
import { ETIQUETA_ETAPA, ORDEN_ETAPAS, type WidgetDataProps } from "../../types";
import { nombreDePeriodo } from "../../utils";

const { Text } = Typography;

const CARD_HEAD   = 38;
const PAD_V       = 18;
const LEGEND_H    = 24;
const MIN_CHART_H = 80;

const STAGE_COLORS: Record<string, string> = {
  Store:       "#13c2c2",
  Cliente:     "#722ed1",
  Seguimiento: "#fa8c16",
  Comprado:    "#52c41a",
};

type Stage = { etapa: string; valor: number };


const fmtK = (v: number) =>
  v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : `$${Math.round(v / 1_000)}K`;

type Datos = {
  /** Sigue al período, como el resto: el monto por etapa de los presupuestos creados en él (D18). */
  medidoPor?: "creacion" | "actividad";
  etapas: { state: string; count: number; amount: number }[];
  total: { count: number; amount: number };
};

export const ValorPipelineChip = ({
  height = 240,
  period = "day",
  data,
  estado,
  error,
  nuncaCalculado,
  recargar,
}: { height?: number; period?: DashboardPeriod } & WidgetDataProps<Datos>) => {
  // El funnel se dibuja de abajo hacia arriba (Store abajo), que es el orden que ya tenía el mock.
  const stages: Stage[] = [...(data?.etapas ?? [])]
    .sort((a, b) => ORDEN_ETAPAS.indexOf(b.state) - ORDEN_ETAPAS.indexOf(a.state))
    .map((e) => ({ etapa: ETIQUETA_ETAPA[e.state] ?? e.state, valor: e.amount }));
  const total  = data?.total?.amount ?? 0;
  const status = estadoDeWidget(estado, error, nuncaCalculado, stages.length > 0, recargar);
  const bodyH  = height - CARD_HEAD;
  const chartH = Math.max(MIN_CHART_H, bodyH - PAD_V - LEGEND_H);

  return (
    <Card
      size="small"
      style={{ height }}
      styles={{ body: { padding: "8px 12px 10px", height: bodyH, overflow: "hidden", display: "flex", flexDirection: "column" } }}
      title={
        <Flex align="center" gap={8}>
          <DollarOutlined style={{ fontSize: 14 }} />
          <span style={{ fontWeight: 600 }}>Valor del pipeline</span>
          <Tooltip
            title="Monto de los presupuestos creados en el período, por la etapa en la que están hoy. Muestra cuánto valor hay en juego antes de llegar al cierre."
            trigger="click"
            color="#fff"
            overlayInnerStyle={{ color: "rgba(0,0,0,0.85)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            overlayStyle={{ maxWidth: 240 }}
          >
            <InfoCircleOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        </Flex>
      }
      extra={
        <Flex align="center" gap={6}>
          <Text type="secondary" style={{ fontSize: 11 }}>{fmtK(total)} total</Text>
          <Text type="secondary" style={{ fontSize: 10, color: "rgba(0,0,0,0.3)" }}>
            {nombreDePeriodo(period)}
          </Text>
        </Flex>
      }
    >
      {status.tipo !== "ok" ? (
        <WidgetPlaceholder status={status} height={bodyH - 20} />
      ) : (
        <>
      <div style={{ height: chartH, overflow: "hidden" }}>
        <Bar
          data={stages}
          xField="valor"
          yField="etapa"
          seriesField="etapa"
          color={({ etapa }: any) => STAGE_COLORS[etapa] ?? "#15744b"}
          barStyle={({ etapa }: any) => ({
            fillOpacity: 0.18,
            stroke: STAGE_COLORS[etapa] ?? "#15744b",
            lineWidth: 1.5,
          })}
          autoFit
          barWidthRatio={0.5}
          xAxis={{
            label: {
              formatter: (v: string) => fmtK(Number(v)),
              style: { fontSize: 9, fill: "rgba(0,0,0,0.35)" },
            },
            grid: { line: { style: { stroke: "#f5f5f5" } } },
            line: null,
            tickLine: null,
          }}
          yAxis={{
            label: { style: { fontSize: 11, fill: "rgba(0,0,0,0.65)" } },
            line: null,
            tickLine: null,
          }}
          tooltip={{
            formatter: (d: any) => ({ name: d.etapa, value: fmtK(d.valor) }),
            domStyles: {
              "g2-tooltip": { background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", border: "none", borderRadius: "6px", padding: "8px 10px", color: "rgba(0,0,0,0.85)", fontSize: "12px" },
            },
          }}
          legend={false}
          padding={[4, 16, 16, 4]}
          animation={false}
        />
      </div>
      <Flex justify="center" align="center" gap={16} style={{ height: LEGEND_H }}>
        {(["Store", "Cliente", "Seguimiento", "Comprado"] as const).map((etapa) => (
          <Flex key={etapa} align="center" gap={4}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: STAGE_COLORS[etapa], display: "inline-block" }} />
            <Text style={{ fontSize: 10, color: "rgba(0,0,0,0.55)" }}>{etapa}</Text>
          </Flex>
        ))}
      </Flex>
            </>
      )}
    </Card>
  );
};
